import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from './contexts/AuthContext';
import { useCredits } from './contexts/CreditsContext';
import Header from './components/common/Header';
import Footer from './components/layout/Footer';
import InsufficientCreditsModal from './components/credits/InsufficientCreditsModal';
import LoginModal from './components/auth/LoginModal';
import RegisterModal from './components/auth/RegisterModal';
import useGlobalPremiumHandler from './hooks/useGlobalPremiumHandler';
import ModalPortal from './components/common/ModalPortal';
import { useAuthModal } from './contexts/AuthModalContext';
import './components/common/HeaderContainer.css';
import './styles/modals.css';

// Pages
import ProfilePlaceholderPage from './pages/ProfilePlaceholderPage';
import HomePage from './pages/HomePage';
import RegisterPage from './pages/auth/RegisterPage';
import LoginPage from './pages/auth/LoginPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ContentCreatorDashboard from './pages/dashboard/ContentCreatorDashboard';
import GeneralUserDashboard from './pages/dashboard/GeneralUserDashboard';
import SuperAdminDashboard from './pages/dashboard/SuperAdminDashboard';
import PageRequests from './pages/PageRequests';
import UserManagementPage from './pages/UserManagementPage'; // Import UserManagementPage
import ManageContentPage from './pages/ManageContentPage';
import PublicPage from './pages/PublicPage'; // New import
import CategoryPage from './pages/CategoryPage'; // Import CategoryPage
import UserPage from './pages/UserPage';
import CreditsPage from './pages/CreditsPage'; // Import CreditsPage
import HowItWorksPage from './pages/HowItWorksPage'; // Import HowItWorksPage
import PostDetailsPage from './pages/PostDetailsPage'; // Import PostDetailsPage
import ContentDetailsPage from './pages/ContentDetailsPage'; // Import ContentDetailsPage for shared track/video URLs
import TermsAndConditionsPage from './pages/TermsAndConditionsPage'; // Import TermsAndConditionsPage
import FreedomOfSpeechPolicy from './pages/FreedomOfSpeechPolicy';
// import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import NotFoundPage from './pages/NotFoundPage';
import CreatorEarningsPage from './pages/CreatorEarningsPage';

// Protected route component
const ProtectedRoute = ({ children, userTypes = [] }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (userTypes.length > 0 && (!user || !user.user_type || !userTypes.includes(user.user_type))) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { isAuthenticated } = useAuth();
  const { refreshBalance } = useCredits();
  const { showLoginModal, showRegisterModal, openLoginModal, closeLoginModal, closeRegisterModal, switchToRegister, switchToLogin } = useAuthModal();
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [modalData, setModalData] = useState({
    requiredCredits: 0,
    currentCredits: 0,
    contentTitle: 'this content',
    contentType: 'content'
  });

  // Global premium card click handler
  useGlobalPremiumHandler(setShowInsufficientCreditsModal, setModalData);
  
  return (
    <div className="App">
      <Helmet>
        <html lang="en" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>
      <Routes>
        {/* Public Routes (no navigation bar) */}
        <Route path="/" element={<HomePage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* How It Works Page */}
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        {/*<Route path="/privacy-policy" element={<PrivacyPolicyPage />} />*/}
        <Route path="/freedom-of-speech-policy" element={<FreedomOfSpeechPolicy />}/>
        {/* Post Details Page */}
        <Route path="/:slug/post/:postId" element={<PostDetailsPage />} />
        {/* Shared Content URLs - Track and Video Details (prefix-first for Azure SWA interception) */}
        <Route path="/track/:pageSlug/:trackId" element={
          <>
            <Header />
            <ContentDetailsPage />
          </>
        } />
        <Route path="/video/:pageSlug/:videoId" element={
          <>
            <Header />
            <ContentDetailsPage />
          </>
        } />
        {/* Legacy shared content URLs — kept for backward compatibility with existing shared links */}
        <Route path="/:pageSlug/track/:trackId" element={
          <>
            <Header />
            <ContentDetailsPage />
          </>
        } />
        <Route path="/:pageSlug/video/:videoId" element={
          <>
            <Header />
            <ContentDetailsPage />
          </>
        } />
        {/* Category route with dynamic ID parameter */}
        <Route path="/:categoryName/:categoryId" element={<CategoryPage />} />
        {/* Public Page Route - IMPORTANT: This handles /:username/:slug */}
        <Route path="/pages/:username" element={<UserPage />} />
        <Route path="/post/:page_slug/:slug" element={<PublicPage />} />

        {/* Protected Routes (with header) */}
        <Route path="/dashboard/*" element={
          <div className="main-background">
            <div className="content-wrapper">
              {isAuthenticated && <Header />}
              <main className="container">
                <Routes>
                  <Route
                    path="content-creator"
                    element={
                      <ProtectedRoute userTypes={['content_creator','super_admin']}>
                        <ContentCreatorDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="general-user"
                    element={
                      <ProtectedRoute userTypes={['general_user']}>
                        <GeneralUserDashboard />
                      </ProtectedRoute>
                    }
                  />
                <Route
                    path="super-admin"
                    element={
                      <ProtectedRoute userTypes={['super_admin']}>
                        <SuperAdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </main>
            </div>
          </div>
        } />

        {/* Credits Route */}
        <Route
          path="/credits"
          element={
            <div className="main-background">
              <div className="content-wrapper">
                {isAuthenticated && <Header />}
                <main className="container">
                  <ProtectedRoute>
                    <CreditsPage />
                  </ProtectedRoute>
                </main>
              </div>
            </div>
          }
        />

        {/* Page Requests Route - Super Admin Only */}
        <Route
          path="/page-requests"
          element={
            <div className="main-background">
              <div className="content-wrapper">
                {isAuthenticated && <Header />}
                <main className="container">
                  <ProtectedRoute userTypes={['super_admin']}>
                    <PageRequests />
                  </ProtectedRoute>
                </main>
              </div>
            </div>
          }
        />

        {/* Creator Earnings Route - Super Admin Only */}
        <Route
          path="/creator-earnings"
          element={
            <div className="main-background">
              <div className="content-wrapper">
                {isAuthenticated && <Header />}
                <main className="container">
                  <ProtectedRoute userTypes={['super_admin']}>
                    <CreatorEarningsPage />
                  </ProtectedRoute>
                </main>
              </div>
            </div>
          }
        />

        {/* User Management Route - Super Admin Only */}
        <Route
          path="/user-management"
          element={
            <div className="main-background">
              <div className="content-wrapper">
                {isAuthenticated && <Header />}
                <main className="container">
                  <ProtectedRoute userTypes={['super_admin']}>
                    <UserManagementPage />
                  </ProtectedRoute>
                </main>
              </div>
            </div>
          }
        />

        {/* Profile Placeholder Route */}
        <Route
          path="/profile"
          element={
            <div className="main-background">
              <div className="content-wrapper">
                {isAuthenticated && <Header />}
                <main className="container">
                  <ProtectedRoute>
                    <ProfilePlaceholderPage />
                  </ProtectedRoute>
                </main>
              </div>
            </div>
          }
        />

        {/* Content Management Routes */}
        <Route path="/manage-content/*" element={
          <div className="main-background">
            <div className="content-wrapper">
              {isAuthenticated && <Header />}
              <main className="container">
                <Routes>
                  <Route
                    path=""
                    element={
                      <ProtectedRoute userTypes={['content_creator','super_admin']}>
                        <ManageContentPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Album Management Route */}
                  <Route
                    path=":postId"
                    element={
                      <ProtectedRoute userTypes={['content_creator','super_admin']}>
                        <ManageContentPage />
                      </ProtectedRoute>
                    }
                  />
                <Route
                    path="super-admin"
                    element={
                      <ProtectedRoute userTypes={['super_admin']}>
                        <SuperAdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </main>
            </div>
          </div>
        } />

        {/* Fallback route - 404 Page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      
      {/* Global Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={modalData.requiredCredits}
        currentCredits={modalData.currentCredits}
        contentTitle={modalData.contentTitle}
        contentType={modalData.contentType}
        onLoginRequired={openLoginModal}
      />

      {/* Global Login / Register Modals */}
      <ModalPortal isOpen={showLoginModal}>
        <LoginModal
          isOpen={showLoginModal}
          onClose={closeLoginModal}
          onLoginSuccess={async () => {
            await refreshBalance();
            closeLoginModal();
          }}
          onSwitchToRegister={switchToRegister}
        />
      </ModalPortal>
      <ModalPortal isOpen={showRegisterModal}>
        <RegisterModal
          isOpen={showRegisterModal}
          onClose={closeRegisterModal}
          onBackToLogin={switchToLogin}
        />
      </ModalPortal>
    </div>
  );
}

export default App;